import { Info } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { formatUsdFromCents } from './formatMoney'

interface OverageInfoTooltipProps {
  /** Credits drawn from the overage pool this cycle. */
  overageCredits: number
  /** Per-credit overage rate (cents) at the org's current plan/interval. */
  rateCents: number
  /** Tooltip placement; defaults to "top". */
  side?: 'top' | 'right' | 'bottom' | 'left'
  /** Accessible label for the trigger button. */
  label?: string
  /** Test id appended to the trigger button. */
  triggerTestId?: string
}

/**
 * Shared `(i)` affordance used by the plan summary card and the team usage
 * row to surface the dollar value of credits consumed past the plan limit.
 */
export function OverageInfoTooltip(props: OverageInfoTooltipProps) {
  const { overageCredits, rateCents, side = 'top', label = 'Overage details', triggerTestId } = props

  if (rateCents <= 0) return null

  const totalCents = overageCredits * rateCents

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          data-testid={triggerTestId}
          className="rounded-full p-0.5 text-muted-foreground hover:text-foreground"
        >
          <Info className="h-3.5 w-3.5" aria-hidden />
        </button>
      </TooltipTrigger>
      <TooltipContent side={side} className="max-w-[240px] text-xs">
        {overageCredits.toLocaleString()} credits over plan
        {' · '}
        <span className="font-medium">{formatUsdFromCents(totalCents)}</span>
        {' '}at {formatUsdFromCents(rateCents)} / credit
      </TooltipContent>
    </Tooltip>
  )
}
